import { useState, useEffect } from "react";
import { Box, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { API_BASE_URL } from "../../config/api";

const formatDateTime = (value) => {
  if (!value) return "N/A";
  try {
    const date = new Date(value);
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    const dateString = String(value).split("T")[0];
    return dateString || value;
  }
};

const formatAdditionalPax = (val) => {
  if (val == null) return "—";
  if (Array.isArray(val)) return val.length ? val.join(", ") : "—";
  return String(val);
};

const LoungeBookings = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLoungeBookings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/admin/lounge/all`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch lounge bookings");
      }

      const data = await response.json();
      if (data.subCode === 200 && Array.isArray(data.items)) {
        setRows(
          data.items.map((item) => ({
            ...item,
            id: item._id,
          }))
        );
      } else {
        setError("Failed to load lounge bookings: " + (data.message || "Unknown error"));
      }
    } catch (err) {
      setError(err.message);
      console.error("Lounge booking fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoungeBookings();
  }, []);

  const columns = [
    {
      field: "createdAt",
      headerName: "Create Date",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: "_id",
      headerName: "ID",
      flex: 1,
      minWidth: 280,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-all", lineHeight: 1.5 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "lounge_code",
      headerName: "Lounge code",
      flex: 0.9,
      minWidth: 110,
    },
    {
      field: "lounge_name",
      headerName: "Lounge name",
      flex: 1.5,
      minWidth: 280,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.5 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "flight_no",
      headerName: "Flight No",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "date_of_visit",
      headerName: "Visit date",
      flex: 0.9,
      minWidth: 110,
    },
    {
      field: "time_of_visit",
      headerName: "Visit time",
      flex: 0.7,
      minWidth: 90,
    },
    {
      field: "pax_full",
      headerName: "Passenger (PAX)",
      flex: 1.2,
      minWidth: 200,
      valueGetter: (params) => {
        const r = params.row;
        return [r.pax_title, r.pax_firstname, r.pax_lastname].filter(Boolean).join(" ");
      },
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.5 }}>
          {[params.row.pax_title, params.row.pax_firstname, params.row.pax_lastname]
            .filter(Boolean)
            .join(" ")}
        </Box>
      ),
    },
    {
      field: "firstName",
      headerName: "First name",
      flex: 0.9,
      minWidth: 110,
    },
    {
      field: "lastName",
      headerName: "Last name",
      flex: 0.9,
      minWidth: 110,
    },
    {
      field: "AdditionalPaxNames",
      headerName: "Additional pax",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.5 }}>
          {formatAdditionalPax(params.value)}
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.4,
      minWidth: 240,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-all" }}>{params.value}</Box>
      ),
    },
    {
      field: "telephone",
      headerName: "Telephone",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "fax",
      headerName: "Fax",
      flex: 0.9,
      minWidth: 120,
    },
    {
      field: "data_protect",
      headerName: "Data protect",
      flex: 0.7,
      minWidth: 100,
    },
    {
      field: "order_total",
      headerName: "Order total",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "currency",
      headerName: "Currency",
      flex: 0.6,
      minWidth: 80,
    },
    {
      field: "addrline1",
      headerName: "Address line 1",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.5 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "addrline2",
      headerName: "Address line 2",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.5 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "city",
      headerName: "City",
      flex: 0.9,
      minWidth: 120,
    },
    {
      field: "province",
      headerName: "Province",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "zip",
      headerName: "Postcode",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "country",
      headerName: "Country",
      flex: 0.6,
      minWidth: 80,
    },
    {
      field: "destination",
      headerName: "Destination",
      flex: 0.8,
      minWidth: 90,
    },
    {
      field: "your_ref",
      headerName: "Your ref",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-all", lineHeight: 1.5 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "result_format",
      headerName: "Result format",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "updatedAt",
      headerName: "Updated",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => formatDateTime(params.value),
    },
  ];

  if (loading) return <Box m="20px">Loading lounge bookings...</Box>;
  if (error) return <Box m="20px">Error: {error}</Box>;

  return (
    <Box m="20px">
      <Header title="LOUNGE BOOKINGS" subtitle="Managing lounge bookings" />
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            display: "flex",
            alignItems: "center",
            py: "8px",
            fontSize: "1rem",
            lineHeight: 1.5,
            color: colors.grey[100],
          },
          "& .MuiDataGrid-row": {
            minHeight: "90px !important",
            maxHeight: "none !important",
            height: "auto !important",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? `${colors.primary[500]} !important`
                  : `rgba(0, 0, 0, 0.04) !important`,
            },
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
            fontSize: "1rem",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              fontSize: "1rem",
            },
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          checkboxSelection
          rows={rows}
          columns={columns}
          autoHeight={false}
          disableRowSelectionOnClick
          disableColumnResize={false}
          initialState={{
            sorting: {
              sortModel: [{ field: "createdAt", sort: "desc" }],
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default LoungeBookings;
